import React, { useEffect, useMemo, useState } from 'react';
import { getTenant, formatCurrency as formatCurrencyFromTokens } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon, InfoIcon, HelpCircleIcon, XIcon as CloseIcon, ChevronLeftIcon, ChevronRightIcon, PenIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, MoreVerticalIcon, PinIcon, LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason, EvidenceDocument } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface RelatedOrder {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  createdTime: string;
  status: string;
  matchingCriteria: number;
  totalCriteria: number;
}
interface MatchingCriteriaDetail {
  category: string;
  subtitle: string;
  items: {
    label: string;
    matched: boolean;
  }[];
}
export function DisputeDetail() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [activeTab, setActiveTab] = useState<'payment' | 'related'>('payment');
  const [selectedOrder, setSelectedOrder] = useState<RelatedOrder | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMatchingCriteriaTooltip, setShowMatchingCriteriaTooltip] = useState(false);
  const [showReasonTooltip, setShowReasonTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showChallengeFlow, setShowChallengeFlow] = useState(false);
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  // State to track if dispute has been accepted
  const [isAccepted, setIsAccepted] = useState(false);
  // State to track if dispute has been challenged (separate from derived isChallenged)
  const [hasBeenChallenged, setHasBeenChallenged] = useState(false);
  // State for adding notes
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [merchantNotes, setMerchantNotes] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    status: 'note';
    author: string;
    isPinned?: boolean;
  }>>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  // Check if returning from challenge submission
  useEffect(() => {
    if (location.state?.challenged) {
      setHasBeenChallenged(true);
    }
  }, [location.state]);
  // Close note menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNoteMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.note-menu-container')) {
          setShowNoteMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNoteMenu]);
  const rowsPerPage = 5;
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeClick = () => {
    navigate(`/disputes/${id}/challenge`);
  };
  const handleViewChallengeDetails = () => {
    setShowChallengeDetails(true);
  };
  const handleChallengeConfirm = () => {
    setShowChallengeModal(false);
    setShowChallengeFlow(true);
    setChallengeStep('reason');
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    // Check if user has provided no evidence at all
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleCloseChallengeFlow = () => {
    setShowChallengeFlow(false);
    setChallengeStep('reason');
    setSelectedChallengeReason('purchasing_history');
    setSelectedProductType('');
    setNoEvidenceChecked(false);
    setNoOrderEvidenceChecked(false);
    setNoCustomerEvidenceChecked(false);
    setNoOtherEvidenceChecked(false);
    setShowNoEvidenceWarning(false);
    setReviewConfirmChecked(false);
  };
  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };
  const handleAcceptConfirm = () => {
    setIsAccepted(true);
    setShowAcceptModal(false);
  };
  const handleAddNote = () => {
    if (noteText.trim()) {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: new Date().toISOString(),
        description: noteText.trim(),
        status: 'note' as const,
        author: 'Eng Han Ng',
        isPinned: false
      };
      setMerchantNotes([newNote, ...merchantNotes]);
      setNoteText('');
      setShowAddNote(false);
    }
  };
  const handleEditNote = (noteId: string) => {
    const note = merchantNotes.find(n => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteText(note.description);
      setShowNoteMenu(null);
    }
  };
  const handleSaveEdit = (noteId: string) => {
    if (editingNoteText.trim()) {
      setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
        ...note,
        description: editingNoteText.trim()
      } : note));
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  const handleDeleteNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.filter(note => note.id !== noteId));
    setShowNoteMenu(null);
  };
  const handlePinNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
      ...note,
      isPinned: !note.isPinned
    } : note));
    setShowNoteMenu(null);
  };
  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedOrderId(orderId);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  // Mock related orders data
  const relatedOrders: RelatedOrder[] = [{
    id: '1',
    orderId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 09:01:43 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '2',
    orderId: 'bcd5898f-5af4-4bb8-99ee-6f2a308d799b',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 09:01:41 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '3',
    orderId: '4086dc8d-fb0b-44d0-8e8c-cf018f0c778d',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:46 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '4',
    orderId: 'a5cf0edf-9412-4ffe-9275-b8914ba25e25',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:33 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '5',
    orderId: '6d92fede-b0c8-4efd-b633-230703873 56f',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:31 UTC+0800',
    status: 'Completed',
    matchingCriteria: 7,
    totalCriteria: 7
  }];
  const matchingCriteriaDetails: MatchingCriteriaDetail[] = [{
    category: 'Customer information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: 'IP address',
      matched: true
    }, {
      label: 'Device ID / fingerprint',
      matched: true
    }, {
      label: 'Shipping address',
      matched: true
    }, {
      label: 'Email address',
      matched: false
    }]
  }, {
    category: 'Transaction information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: '120 - 365 days older',
      matched: true
    }, {
      label: 'Undisputed & not fraudulent',
      matched: true
    }]
  }, {
    category: 'Additional information',
    subtitle: '',
    items: [{
      label: 'Payment method',
      matched: true
    }]
  }];
  const totalPages = Math.ceil(relatedOrders.length / rowsPerPage);
  const paginatedOrders = relatedOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  // Find the dispute by ID using centralized data
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      // Return first dispute as fallback
      return allDisputes[0];
    }
    return found;
  }, [id, allDisputes]);
  // Check if dispute is challenged - either from the dispute status or from navigation state
  const isChallenged = dispute.status === 'challenged' || location.state?.challenged === true || hasBeenChallenged;
  // Get effective status (use accepted if user just accepted it)
  const effectiveStatus = isAccepted ? 'accepted' : dispute.status;
  const paymentAmount = dispute.amount;
  const disputedAmount = dispute.amount;
  const disputeFee = Math.round(dispute.amount * 0.08);
  const netAmount = disputedAmount + disputeFee;
  const formatCurrency = (amount: number, currency: string = getTenant().currency.code) => {
    return formatCurrencyFromTokens(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      requires_response: 'text-[#E2231A] bg-[#FEE7E5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#C41B14] bg-[#FBD4D1]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${styles[status]}`} data-id="element-6333" data-mp="367o3diznzqi4">
        {labels[status]}
      </span>;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getReasonDescription = (reason: DisputeReason): string => {
    const descriptions: Record<DisputeReason, string> = {
      duplicate: 'The customer claims this is a duplicate charge',
      fraudulent: 'The customer claims the transaction was fraudulent',
      subscription_canceled: 'The customer claims they canceled their subscription',
      product_unacceptable: 'The customer claims the product was unacceptable',
      product_not_received: 'The customer claims they did not receive the product',
      unrecognized: 'The customer does not recognize the transaction',
      credit_not_processed: 'The customer claims a credit was not processed',
      incorrect_account_details: 'The customer claims incorrect account details were used',
      insufficient_funds: 'The customer claims they had insufficient funds',
      bank_cannot_process: 'The bank cannot process the transaction',
      debit_not_authorized: 'The customer claims the debit was not authorized',
      general: 'General dispute reason',
      customer_initiated: 'Customer initiated the dispute',
      check_returned: 'The check was returned',
      noncompliant: 'The transaction was noncompliant'
    };
    return descriptions[reason] || 'Dispute reason not specified';
  };
  const getReasonLabel = (reason: DisputeReason): string => {
    const labels: Record<DisputeReason, string> = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentSource = () => {
    // In a real app, this would come from the dispute data
    // For now, we'll use a simple logic based on dispute ID
    const sources = ['Payment Link', 'QR Code', 'Invoice', 'Tap to Phone', 'POS Terminal'];
    const sourceIcons = [LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon];
    const index = parseInt(dispute.id) % sources.length;
    return {
      name: sources[index],
      Icon: sourceIcons[index]
    };
  };
  const hoursRemaining = getHoursRemaining(dispute.dueDate);
  const reversedLifecycle = useMemo(() => {
    const lifecycle = [...dispute.lifecycle];
    if (isChallenged) {
      lifecycle.push({
        id: '3',
        timestamp: new Date().toISOString(),
        status: 'challenged' as DisputeStatus,
        description: 'Dispute challenged with supporting evidence'
      });
    }
    if (isAccepted) {
      lifecycle.push({
        id: String(lifecycle.length + 1),
        timestamp: new Date().toISOString(),
        status: 'accepted' as DisputeStatus,
        description: 'Dispute accepted by merchant'
      });
    }
    // Separate pinned and unpinned notes
    const pinnedNotes = merchantNotes.filter(note => note.isPinned);
    const unpinnedNotes = merchantNotes.filter(note => !note.isPinned);
    // Sort unpinned notes by timestamp
    const sortedUnpinnedNotes = unpinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Combine: pinned notes first (sorted by timestamp), then lifecycle events and unpinned notes
    const allEvents = [...lifecycle, ...sortedUnpinnedNotes];
    const sortedEvents = allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Add pinned notes at the top
    return [...pinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()), ...sortedEvents];
  }, [dispute.lifecycle, isChallenged, isAccepted, merchantNotes]);
  // Get evidence recommendations using the centralized utility
  const evidenceRecs = useMemo(() => {
    // LOGGING: Start of evidence recommendation lookup
    console.group('🔍 Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand (raw):', dispute.cardBrand);
    console.log('Network Reason Code (raw):', dispute.networkReasonCode);
    console.log('Dispute Reason:', dispute.reason);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  // If challenge flow is active, show full-screen challenge interface
  if (showChallengeFlow) {
    return <div className="min-h-screen bg-white" data-id="element-6334" data-mp="f4onoezgp37g">
        {/* Header */}
        <div className="border-b border-gray-200" data-id="element-6335" data-mp="1e34ep63ir91">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-6336" data-mp="1da5ubhaj81z">
            <div className="flex items-center gap-3" data-id="element-6337" data-mp="66wb3il4k9nr">
              <div className="flex items-center gap-2" data-id="element-6338" data-mp="3nmbcedk216ym">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6339" data-mp="3njq9dl80skza">
                  <path d="M3 7L12 2L21 7L12 12L3 7Z" fill="#E2231A" stroke="#E2231A" strokeWidth="2" strokeLinejoin="round" data-id="element-6340" data-mp="3jk7zdkhqlub8" />

                  <path d="M3 12L12 17L21 12" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6341" data-mp="2v0mrre1i0x0t" />

                  <path d="M3 17L12 22L21 17" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6342" data-mp="34zvhfupbzphd" />
                </svg>
                <span className="text-lg font-semibold" data-id="element-6343" data-mp="3smk4svx40a5d">
                  {t('pages.DisputeDetail.pomelo_pay')}
                </span>
              </div>
              <span className="text-gray-400" data-id="element-6344" data-mp="5i4687lrnste">|</span>
              <span className="text-gray-700" data-id="element-6345" data-mp="5inh54d01gxx">
                {t('pages.ChallengeDispute.challenge_dispute')}
              </span>
            </div>
            <button onClick={handleCloseChallengeFlow} className="text-gray-400 hover:text-gray-600" data-id="element-6346" data-mp="2ubsnijl1g6uv">
              <CloseIcon size={24} data-id="element-6347" data-mp="2zavl5iyovast" />
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-6348" data-mp="33b5o0mr6pl3g">
          <div className="flex gap-8" data-id="element-6349" data-mp="382faxflcmabn">
            {/* Left Sidebar */}
            <div className="w-64 flex-shrink-0" data-id="element-6350" data-mp="384s3emoqg32y">
              <div className="space-y-1" data-id="element-6351" data-mp="3d2js77u2z92x">
                <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${expandedSections.challengeDetails ? 'bg-gray-100' : ''}`} onClick={() => toggleSection('challengeDetails')} data-id="element-6352" data-mp="3d3uwd8rfuiuk">
                  <span className="font-medium" data-id="element-6353" data-mp="3r08bib8i285v">
                    {t('pages.ChallengeDispute.challenge_details')}
                  </span>
                  {expandedSections.challengeDetails ? <ChevronUpIcon size={16} data-id="element-6354" data-mp="1wo0orl6c0xl6" /> : <ChevronDownIcon size={16} data-id="element-6355" data-mp="1p499s4p9x8bj" />}
                </button>
                {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-6356" data-mp="1kb6ffuco9uua">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-6357" data-mp="1fdepv6zi2kt9">
                      {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6358" data-mp="1u3lw8oc8gddl" />}
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6359" data-mp="ine2nces4ce2">
                      {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6360" data-mp="21mk1to68hiv2" />}
                      {t('pages.ChallengeDispute.supporting_evidence')}
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-6361" data-mp="2u7tqyt8wwv9m">
                  <span data-id="element-6362" data-mp="14uq8qgiq4u8x">{t('pages.ChallengeDispute.transaction_details')}</span>
                  {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-6363" data-mp="19vxds7thffbw" /> : <ChevronDownIcon size={16} data-id="element-6364" data-mp="11camvfjr08c8" />}
                </button>
                {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-6365" data-mp="xe34kg9myyyx">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6366" data-mp="1bagodw1n7cgk">
                      {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6367" data-mp="seqkg3a5g601" />}
                      {t('pages.ChallengeDispute.order_details')}
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6368" data-mp="3vzw703qpjuv4">
                      {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6369" data-mp="24pugxiem9sbu" />}
                      {t('pages.ChallengeDispute.customer_details')}
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-6370" data-mp="1gzqehed5zymf">
                  <span data-id="element-6371" data-mp="1q0ohpufs423y">{t('pages.ChallengeDispute.other_evidence')}</span>
                  {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-6372" data-mp="2wfyuax3ylf3k" /> : <ChevronDownIcon size={16} data-id="element-6373" data-mp="31es8krr2la4s" />}
                </button>
                {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-6374" data-mp="3blmh5nbrbrcz">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6375" data-mp="36k5mi9jv914u">
                      {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6376" data-mp="2nu7axjgv0pwg" />}
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-6377" data-mp="h3rg6h7hgid0">
              {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-6378" data-mp="d1cy3zbn1mms">
                  <div className="flex items-start justify-between mb-6" data-id="element-6379" data-mp="d2nzxtliwkb9">
                    <div data-id="element-6380" data-mp="wbp91o3gfiho">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6381" data-mp="wbfrmowh6jnl">
                        {t('pages.ChallengeDispute.challenge_reason')}
                      </h1>
                      <p className="text-gray-600" data-id="element-6382" data-mp="mk1n22po2tnl">
                        {t('pages.DisputeDetail.your_responses_will_help_us_recommend')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6383" data-mp="cozxj5rhbpkh">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6384" data-mp="hmt07otkq1g0">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6385" data-mp="8luxraysa1zj">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6386" data-mp="22y1jv4t7u36m">
                        <div className="text-gray-500" data-id="element-6387" data-mp="22wqg34zrsvfi">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6388" data-mp="2857zdnhrpo7t">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6389" data-mp="2h435z0oq50bn">
                        <div className="text-gray-500" data-id="element-6390" data-mp="2h2ic7da8hleu">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6391" data-mp="2qv7ky0eegl6h">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6392" data-mp="34s38exveef62">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6393" data-mp="16f2w2401wn2x">
                    <div data-id="element-6394" data-mp="16ge10758g0vm">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-6395" data-mp="fcrzs16vlnca">
                        {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-6396" data-mp="5ldt99gibn9n">
                        {t('pages.ChallengeDispute.choose_the_closest_option')}
                      </p>
                      <div className="space-y-3" data-id="element-6397" data-mp="3sry16v8ipvta">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6398" data-mp="3splbtx0fkj67">
                          <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-6399" data-mp="3isysjcnr606a" />

                          <span className="text-sm text-gray-900" data-id="element-6400" data-mp="1ki8bsys4l4b2">
                            {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6401" data-mp="1ydk7b8e3uyt1">
                          <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-6402" data-mp="15miqz3yoex7x" />

                          <span className="text-sm text-gray-900" data-id="element-6403" data-mp="2r27uj9iezy98">
                            {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer" data-id="element-6404" data-mp="2b9dw30z5q16d">
                          <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-6405" data-mp="1il8bqfc0me72" />

                          <div className="flex-1" data-id="element-6406" data-mp="uve0213fmvoe">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-6407" data-mp="36jiuxkoncizi">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </div>
                            {selectedChallengeReason === 'purchasing_history' && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-6408" data-mp="3pa09mlcn9e1v">
                                <p className="text-sm text-gray-700" data-id="element-6409" data-mp="3u8trepyyrr7u">
                                  {t('pages.DisputeDetail.we_recommend_this_reason_as_we')}
                                </p>
                              </div>}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6410" data-mp="28vik2wln68fo">
                          <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-6411" data-mp="2p3onuz35zrmz" />

                          <span className="text-sm text-gray-900" data-id="element-6412" data-mp="332o2ddwyqq5f">
                            {t('pages.ChallengeDispute.other_reasons')}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-6413" data-mp="2u16nfyvakuib">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-6414" data-mp="2u42lrob1xl7a">
                        {t('pages.DisputeDetail.what_type_of_productservice_was_the')}
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-6415" data-mp="3ub7qahthb3r0">
                        {t('pages.ChallengeDispute.choose_the_closest_option')}
                      </p>
                      <div className="space-y-3" data-id="element-6416" data-mp="74ovgrqe3x1v">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6417" data-mp="73vyujl5anxg">
                          <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-6418" data-mp="gva292g7w7vv" />

                          <div data-id="element-6419" data-mp="2row3ndgmc8mb">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-6420" data-mp="2wm603d2fdkjn">
                              {t('pages.ChallengeDispute.physical_product')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-6421" data-mp="3uxyv61mwcofk">
                              {t('pages.DisputeDetail.physical_goods_to_be_delivered_to')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6422" data-mp="mq08q6rkhpr7">
                          <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-6423" data-mp="hqxfrkov39zl" />

                          <div data-id="element-6424" data-mp="1k36z07n3cogb">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-6425" data-mp="1k51j5jqjgcjc">
                              {t('pages.ChallengeDispute.digital_product_or_service')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-6426" data-mp="1f443pewwxwlj">
                              {t('pages.DisputeDetail.purchased_access_to_software_digital_files')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6427" data-mp="rgdrn6f74qxc">
                          <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-6428" data-mp="wbt3jstn8cr4" />

                          <div data-id="element-6429" data-mp="svzzotbwf4x8">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-6430" data-mp="sze384watk69">
                              {t('pages.ChallengeDispute.offline_service')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-6431" data-mp="jxxtlzdb5m33">
                              {t('pages.DisputeDetail.labour_or_assistant_services_provided_either')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6432" data-mp="8dwhm6l22aw">
                          <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-6433" data-mp="3sdpvuf6zdpo2" />

                          <div data-id="element-6434" data-mp="1o6oh8vyv4f5g">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-6435" data-mp="1o7psb0fvp1n6">
                              {t('pages.ChallengeDispute.reservation_or_booking')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-6436" data-mp="22v14n4jvwpoc">
                              {t('pages.DisputeDetail.services_related_to_reservations_or_bookings')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-6437" data-mp="3mmngzrx4q43m">
                          <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-6438" data-mp="3ri2swebktpxe" />

                          <span className="text-sm text-gray-900" data-id="element-6439" data-mp="j5x3yt34qqsf">
                            {t('pages.ChallengeDispute.other')}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6440" data-mp="2pua4ez4bmj1c">
                    <button onClick={handleCloseChallengeFlow} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6441" data-mp="17078a3o7cq5f">
                      {t('pages.ChallengeDispute.cancel')}
                    </button>
                    <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6442" data-mp="t9bx2kq8dwyo">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-6443" data-mp="5jrc5waiopmr">
                  <div className="flex items-start justify-between mb-6" data-id="element-6444" data-mp="jv8xz9n9289">
                    <div data-id="element-6445" data-mp="ik5k0k41who">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6446" data-mp="210xc4vagb3l">
                        {evidenceRecs.title}
                      </h1>
                      <p className="text-gray-600" data-id="element-6447" data-mp="6yu532c8pd5z">
                        {evidenceRecs.description}
                        {evidenceRecs.learnMoreLink && <>
                            {' '}
                            <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-6448" data-mp="3u6xrc96cjmor">
                              {evidenceRecs.learnMoreLink}
                            </a>
                          </>}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6449" data-mp="3ahnfj69q1lvx">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6450" data-mp="2wh2rnvoy8zvk">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6451" data-mp="2hu42k7dkehk0">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6452" data-mp="2rsjx6kbsw8y9">
                        <div className="text-gray-500" data-id="element-6453" data-mp="2rtbgavo7oe7n">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6454" data-mp="30syk1o4exw3v">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6455" data-mp="2vs2g5ak2a9yg">
                        <div className="text-gray-500" data-id="element-6456" data-mp="3ajb5rjnyrvbl">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6457" data-mp="35mawz0zqcegs">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6458" data-mp="3fj5t5d8j62rw">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-6459" data-mp="1hsh4p55m498j">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6460" data-mp="1mkquml6q99at">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    {/* LOGGING: Verify evidence is being rendered */}
                    {console.log('🎨 Rendering evidence step with:', {
                  title: evidenceRecs.title,
                  recommendationsCount: evidenceRecs.recommendations.length,
                  firstRecommendation: evidenceRecs.recommendations[0]
                })}
                    {dispute.networkReasonCode === '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-6461" data-mp="2f8wgjb9ip4c8">
                        {t('pages.DisputeDetail.for_visa_132_subscription_canceled_disputes')}
                      </p>}
                    {dispute.reason === 'subscription_canceled' && dispute.networkReasonCode !== '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-6462" data-mp="3v08f3s2zra4v">
                          {t('pages.DisputeDetail.for_subscription_cancellation_disputes_we_recommend')}
                        </p>}
                    {dispute.reason !== 'subscription_canceled' && <p className="text-sm text-gray-600 mb-4" data-id="element-6463" data-mp="3g6c6rt6u6hhn">
                        {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                      </p>}
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-6464" data-mp="1a9zq4kivq48p">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-6465" data-mp="1ogtay03a7f88" data-mpi={index}>
                          <strong data-id="element-6466" data-mp="1ofrrp8c43mff">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                    <p className="text-sm text-gray-500" data-id="element-6467" data-mp="1fh6cnn5umpje">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-6468" data-mp="2c546cnq7kmp4" />
                            {requirements.language}
                            <br data-id="element-6469" data-mp="1y75upq39v4db" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-6470" data-mp="mqxx832p6j8i">
                    <button disabled={noEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6471" data-mp="mtaf6g8ggtlk">
                      <UploadIcon size={16} data-id="element-6472" data-mp="t7poppaghryx" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div className="mb-6" data-id="element-6473" data-mp="is9xw5gedax">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6474" data-mp="jtviuk3ii6w" />
                  </div>

                  <div className="mb-6" data-id="element-6475" data-mp="2ocs2ueghh10g">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6476" data-mp="2odtsbny8xq1p">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6477" data-mp="2sdu38ym4zj40" />

                      <span className="text-sm text-gray-900" data-id="element-6478" data-mp="37auuz24bve2h">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6479" data-mp="3g617ejy3sudz">
                    <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6480" data-mp="3l4vo9gcig8vg">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6481" data-mp="1jbiqz0kvp8f4">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-6482" data-mp="1x5a1ab6fowbw">
                  <div className="flex items-start justify-between mb-6" data-id="element-6483" data-mp="1x6bh0syea6zy">
                    <div data-id="element-6484" data-mp="1se1qpbtdb4x5">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6485" data-mp="1sd8tb1gatrro">
                        {t('pages.ChallengeDispute.order_details')}
                      </h1>
                      <p className="text-gray-600" data-id="element-6486" data-mp="2kve6gmz9t450">
                        {t('pages.DisputeDetail.please_provide_as_much_information_as')}
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-6487" data-mp="3hu2qrwav6kyx">
                        {t('pages.DisputeDetail.the_details_below_have_been_prefilled')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6488" data-mp="3qrd5jrmw4s8l">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6489" data-mp="3mr1n8li0340a">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6490" data-mp="3vrzncspbirdl">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6491" data-mp="9e99vgcmf7hc">
                        <div className="text-gray-500" data-id="element-6492" data-mp="9fkfzmtjgrbm">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6493" data-mp="if7f21yspl1i">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6494" data-mp="16zau8175ciz5">
                        <div className="text-gray-500" data-id="element-6495" data-mp="16wonu4vzs9ii">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6496" data-mp="wztqhpbggf5t">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6497" data-mp="j73qui307bq8">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6498" data-mp="e9kfbpxoxd1z">
                    <div data-id="element-6499" data-mp="eadb5vv42i5c">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6500" data-mp="acbgjql1grg">
                        {t('pages.ChallengeDispute.product_details')}
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-6501" data-mp="58vzcpoql2mg">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6502" data-mp="26zmnj1dtxfig">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6503" data-mp="1t7zjughk54pn" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6504" data-mp="15idhjk5w29df">
                        <span data-id="element-6505" data-mp="10hhflc53kxan">
                          {t('pages.DisputeDetail.iphone7_5_10001_usd')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6506" data-mp="3c7p73ylyzbzg">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6507" data-mp="3h5h1cza37a6t">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6508" data-mp="3uvkgz7ego5na" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6509" data-mp="1o7a61zhw4rhe">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6510" data-mp="1o822tdn84v99">
                        {t('pages.ChallengeDispute.shipping_address')}
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-6511" data-mp="vid08vuv8n5j">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6512" data-mp="vgibf6gh4aw6">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6513" data-mp="hovfj7qg14dx" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6514" data-mp="2jdrmaf3h47al">
                        <span data-id="element-6515" data-mp="373lsidtaexlv">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6516" data-mp="2y7cth0982109">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6517" data-mp="332a3a9ebsee7">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6518" data-mp="3tdlydsebyjkd" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6519" data-mp="uw86n9vk059n">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6520" data-mp="uwzrpqriyklo">
                        {t('pages.ChallengeDispute.shipping_carrier')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6521" data-mp="2hs6439tlsbjc">
                        <span data-id="element-6522" data-mp="2mjo33sc2g21o">{t('pages.ChallengeDispute.sameday')}</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6523" data-mp="2ql9o60yr7mmy">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6524" data-mp="2qncc8fgcj5p1">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6525" data-mp="22y1k1md2ajmi" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6526" data-mp="38o8a412ozjrd">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6527" data-mp="38orq3c8s7y2r">
                        {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6528" data-mp="1sa0015z3z2wm">
                        {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                        <span className="font-medium" data-id="element-6529" data-mp="1ob9cnqkg2zmp">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                        {t('pages.DisputeDetail.we_recommend_you_to_also_provide')}
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-6530" data-mp="19ej97ntboujs">
                        <li data-id="element-6531" data-mp="19c6mkxcmdw0e">{t('pages.ChallengeDispute.shipping_date')}</li>
                        <li data-id="element-6532" data-mp="ufxnpwwn5koi">
                          {t('pages.ChallengeDispute.shipping_tracking_number')}
                        </li>
                      </ul>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6533" data-mp="lhlzu2n5t0zr">
                        {t('pages.DisputeDetail.you_can_upload_any_document_containing')}
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6534" data-mp="2fvd38kewaomr">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6535" data-mp="3t5vx0y899zxb" />
                              {requirements.language}
                              <br data-id="element-6536" data-mp="3eh9eyor8uu2e" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6537" data-mp="ax53utvm5tpk">
                      <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6538" data-mp="fxswtl3ekkmf">
                        <UploadIcon size={16} data-id="element-6539" data-mp="2mqv8hdlk8k0r" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-6540" data-mp="2j507xawtor36">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6541" data-mp="2j8e9wi1kqya3" />
                    </div>

                    <div data-id="element-6542" data-mp="2a77r22bgdbdo">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6543" data-mp="25f6juut56aui">
                        <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6544" data-mp="25d3ixfqgaxb0" />

                        <span className="text-sm text-gray-900" data-id="element-6545" data-mp="26dvpaqdvr5oe">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6546" data-mp="1nh46ei292skv">
                    <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6547" data-mp="1sfxlug13ktno">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6548" data-mp="38u79t91fggdz">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-6549" data-mp="ij8wwcfb796z">
                  <div className="flex items-start justify-between mb-6" data-id="element-6550" data-mp="igee0pby1wfo">
                    <div data-id="element-6551" data-mp="ehnd074cxuna">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6552" data-mp="eesuwm8tglx1">
                        {t('pages.ChallengeDispute.customer_details')}
                      </h1>
                      <p className="text-gray-600" data-id="element-6553" data-mp="4j7xz2ilubu5">
                        {t('pages.DisputeDetail.please_provide_as_much_information_as_2')}
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-6554" data-mp="1yumnpjbm8b9w">
                        {t('pages.DisputeDetail.the_details_below_have_been_prefilled')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6555" data-mp="26xooa1m1n5gq">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6556" data-mp="26vc5jm8gor2m">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6557" data-mp="1x124zh9d8mgt">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6558" data-mp="1nzvmj2n5p1kx">
                        <div className="text-gray-500" data-id="element-6559" data-mp="19afw5nrjey5r">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6560" data-mp="1e6eofmfzl0bg">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6561" data-mp="14a1xgwbtt0ke">
                        <div className="text-gray-500" data-id="element-6562" data-mp="llzvmudox2bu">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6563" data-mp="gp0ye2yqcf9f">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6564" data-mp="po4mjlcr7kus">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6565" data-mp="zlizvc3drtiv">
                    <div data-id="element-6566" data-mp="1dd7ble9pwfzb">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6567" data-mp="1ddp7g9sgws6p">
                        {t('pages.ChallengeDispute.customer_name')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6568" data-mp="1nb3lk2qx54vu">
                        <span data-id="element-6569" data-mp="1ics9dld5iiag">{t('pages.ChallengeDispute.test_name')}</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6570" data-mp="1iapaebu0tetl">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6571" data-mp="g0ysgr3940xg">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6572" data-mp="ts3lhac45uyt" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6573" data-mp="2x6w09vfkw521">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6574" data-mp="2x877k588fuxw">
                        {t('pages.ChallengeDispute.customer_email')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6575" data-mp="3pv1nqpy0o85k">
                        <span data-id="element-6576" data-mp="3kwqeohfjq1oe">
                          {t('pages.DisputeDetail.jessicapomelopaycom')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6577" data-mp="3fwc5gw27klos">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6578" data-mp="3bva6mgc7ebnc">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6579" data-mp="3g92fbma4yr6" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6580" data-mp="1au3w4fro3w1u">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6581" data-mp="1auc885fyuxbz">
                        {t('pages.DisputeDetail.customer_purchase_ip')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6582" data-mp="1g7u4nqd5v08b">
                        <span data-id="element-6583" data-mp="1tyfqnnxwvqaf">35.221.170.121</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6584" data-mp="1ty7czttysgy6">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6585" data-mp="1p0nom13jynsi">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6586" data-mp="1b8hi76q7bv69" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6587" data-mp="p8gt5g9hs7dx">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6588" data-mp="p7yr2305eyy3">
                        {t('pages.DisputeDetail.customer_phone')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6589" data-mp="fix7y3yb41fw">
                        <span data-id="element-6590" data-mp="1cy2qdy6b0cja">+65 9123 4567</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6591" data-mp="1cyuobfndimcq">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6592" data-mp="1872tgsx6pugs">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6593" data-mp="1pg5e1m1x869k" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6594" data-mp="m2bv5yxqul5u">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6595" data-mp="i3cqmffxcqex">
                        {t('pages.ChallengeDispute.customer_billing_address')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6596" data-mp="r416lfovp0ug">
                        <span data-id="element-6597" data-mp="15snmpjmbxwmq">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6598" data-mp="1et3nglboupnw">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6599" data-mp="1eqzmab56c4rb">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6600" data-mp="3bnqcoo5rk3z2" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6601" data-mp="2trhl3xx88bpi">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6602" data-mp="2tr7uc84cy0ir">
                        {t('pages.ChallengeDispute.provide_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6603" data-mp="242keo2wfrlrs">
                        {t('pages.DisputeDetail.we_recommend_you_to_provide_the')}
                      </p>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6604" data-mp="1u33ajpwu1fxx">
                        {t('pages.DisputeDetail.you_can_upload_any_document_containing_2')}
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6605" data-mp="1l9q89xcvp1z6">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6606" data-mp="16gmxq9xpfohy" />
                              {requirements.language}
                              <br data-id="element-6607" data-mp="12dqlr22o6qmz" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6608" data-mp="3i3qfljicef8p">
                      <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6609" data-mp="3n0gzyj16jwzx">
                        <UploadIcon size={16} data-id="element-6610" data-mp="34ah9brpoy9ue" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-6611" data-mp="392inxkac0cnl">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6612" data-mp="2bltlbodk56d5" />
                    </div>

                    <div data-id="element-6613" data-mp="2dd9vuwavla05">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6614" data-mp="1pktcmifejbz9">
                        <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6615" data-mp="1ljrbtx5u6rv6" />

                        <span className="text-sm text-gray-900" data-id="element-6616" data-mp="3604d7kcebt9e">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6617" data-mp="irx2jfnvaxc0">
                    <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6618" data-mp="nr9qy4ue0cgp">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6619" data-mp="11oyexljr3zpz">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-6620" data-mp="3v9bknw2ozeg1">
                  <div className="flex items-start justify-between mb-6" data-id="element-6621" data-mp="3qhtjb6wrbbuj">
                    <div data-id="element-6622" data-mp="3qgicszbxfpzq">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6623" data-mp="3liqf1oolp9nm">
                        {t('pages.ChallengeDispute.other_supporting_documents')}
                      </h1>
                      <p className="text-gray-600" data-id="element-6624" data-mp="3ghsvb7ny07k0">
                        {t('pages.DisputeDetail.if_you_have_any_other_documents')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6625" data-mp="832iy2fy2v0t">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6626" data-mp="85x0nm7kq1qj">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6627" data-mp="2srq8p72srtss">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6628" data-mp="14c9stdlu0z9r">
                        <div className="text-gray-500" data-id="element-6629" data-mp="18b8yj0fe1029">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6630" data-mp="1d9af9epccnak">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6631" data-mp="1n0etgc77044c">
                        <div className="text-gray-500" data-id="element-6632" data-mp="1rzrh2z5w1f7z">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6633" data-mp="1w2eb96q17c8c">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6634" data-mp="1ntyrhbv6n01">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6635" data-mp="2uo6uzlrafbno">
                    <div data-id="element-6636" data-mp="2znjhg5e8ygpq">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6637" data-mp="2zn01gu85q2ec">
                        {t('pages.ChallengeDispute.provide_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6638" data-mp="38i7ljs835scy">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6639" data-mp="2ksn114waan1k" />
                              {requirements.language}
                              <br data-id="element-6640" data-mp="2pqwxditqvtor" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6641" data-mp="517kvcgikjz8">
                      <button disabled={noOtherEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6642" data-mp="f3uomb0m2onn">
                        <UploadIcon size={16} data-id="element-6643" data-mp="1a3o1s4uyf42" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-6644" data-mp="3nhtklcnl2a15">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6645" data-mp="3nlhck7ddqodr" />
                    </div>

                    <div data-id="element-6646" data-mp="3b2lsabciplj2">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6647" data-mp="2x946ejwczwpv">
                        <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6648" data-mp="2x7b055pnreie" />

                        <span className="text-sm text-gray-900" data-id="element-6649" data-mp="3kyotnezjpg4y">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6650" data-mp="2129dbikhtwse">
                    <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6651" data-mp="1x1h24js29hth">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6652" data-mp="1ibi5ilg5meg4">
                      {t('pages.ChallengeDispute.review')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-6653" data-mp="zks4z3e233qf">
                  <div className="flex items-start justify-between mb-6" data-id="element-6654" data-mp="zjgw4p5r75sg">
                    <div data-id="element-6655" data-mp="ulxldz89lb59">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6656" data-mp="ukcnbzmqra2n">
                        {t('pages.ChallengeDispute.review_your_challenge')}
                      </h1>
                      <p className="text-gray-600" data-id="element-6657" data-mp="2ues3u9ygtos">
                        {t('pages.DisputeDetail.please_confirm_that_the_information_you')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6658" data-mp="gltkusto0991">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6659" data-mp="gl1upxiu73q5">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6660" data-mp="qkiy28am95iy">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6661" data-mp="2scowgwmj86f7">
                        <div className="text-gray-500" data-id="element-6662" data-mp="2wcfy5vdlk6yi">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-6663" data-mp="2ouhpft38ozwc">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6664" data-mp="2f4ex7v3xzpz2">
                        <div className="text-gray-500" data-id="element-6665" data-mp="2b1s2nmfvzqy6">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-6666" data-mp="37qr86tp36emo">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6667" data-mp="2yrcme7jv6s8i">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6668" data-mp="1nd8quwxmow1c">
                    {/* Challenge details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-6669" data-mp="1ic32tcewxwy5">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6670" data-mp="1jujhcf12yf24">
                        {t('pages.ChallengeDispute.challenge_details')}
                      </h3>
                      <div className="space-y-3" data-id="element-6671" data-mp="1or0etzu665rv">
                        <div className="flex justify-between" data-id="element-6672" data-mp="1ask0pntz9czp">
                          <span className="text-sm text-gray-600" data-id="element-6673" data-mp="1eux2601oyoqo">
                            {t('pages.ChallengeDispute.reason_for_challenge')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6674" data-mp="111z3a3q1ieid">
                            {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6675" data-mp="w4yzjzgnxnuf">
                          <span className="text-sm text-gray-600" data-id="element-6676" data-mp="i5z8ym9layvm">
                            {t('pages.ChallengeDispute.type_of_product_or_service')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6677" data-mp="3k2700shwti5">
                            {t('pages.ChallengeDispute.physical_product')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-6678" data-mp="mh3blki50rlk">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6679" data-mp="mf8rg8eox3ij">
                        {t('pages.ChallengeDispute.transaction_details')}
                      </h3>
                      <div className="space-y-3" data-id="element-6680" data-mp="w5la1v2y2mm1">
                        <div className="flex" data-id="element-6681" data-mp="r7tgyxqkcug9">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6682" data-mp="r94jyvc6py6b">
                            {t('pages.ChallengeDispute.product_details')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6683" data-mp="109jbwdjnmbis">
                            {t('pages.ChallengeDispute.iphone7_5')}{' '}
                            {formatCurrency(dispute.amount)}
                          </span>
                        </div>
                        <div className="flex" data-id="element-6684" data-mp="2glvfa1adobo9">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6685" data-mp="2gm56fs75sovj">
                            {t('pages.ChallengeDispute.shipping_address')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6686" data-mp="1ovqvo4ufzso3">
                            {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-6687" data-mp="22m43863f95cw">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6688" data-mp="22nxfc0wt0hsa">
                            {t('pages.ChallengeDispute.customer_name')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6689" data-mp="1a3qjfaex7jwt">
                            {t('pages.ChallengeDispute.test_name')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-6690" data-mp="xnqq2irho5h2">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6691" data-mp="snbf1xuoja29">
                            {t('pages.ChallengeDispute.customer_email')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6692" data-mp="jmwjm5o04el1">
                            {t('pages.DisputeDetail.customerpomelopaycom')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-6693" data-mp="9wtpg2558ul4">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6694" data-mp="9xmpkk9lklua">
                            {t('pages.ChallengeDispute.customer_purchase_ip')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6695" data-mp="vvknhmvsj1h">
                            35.221.170.121
                          </span>
                        </div>
                        <div className="flex" data-id="element-6696" data-mp="3105aql8tg31j">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6697" data-mp="312zt8789rdsb">
                            {t('pages.ChallengeDispute.customer_billing_address')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6698" data-mp="361ktu7rfnzha">
                            {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no evidence */}
                    {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-6699" data-mp="1ulw6vge6j0gh">
                        <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-6700" data-mp="a20r7smt7hea" />

                        <p className="text-sm text-gray-900" data-id="element-6701" data-mp="5e8pahyh2ff">
                          {t('pages.DisputeDetail.please_attach_at_least_one_of')}
                        </p>
                      </div>}

                    {/* Confirmation checkbox */}
                    <div data-id="element-6702" data-mp="xrbpn3goszp9">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6703" data-mp="xqk0obhli07y">
                        <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-[#C41B14] border-[#FBD4D1] rounded focus:ring-[#E2231A]" data-id="element-6704" data-mp="jsbwbii8bwmr" />

                        <span className="text-sm text-gray-900" data-id="element-6705" data-mp="1f1796be54wpm">
                          {t('pages.DisputeDetail.i_understand_that_the_current_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6706" data-mp="wbhc1hmwnyuj">
                    <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6707" data-mp="rce9q4yb86q9">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-[#E2231A] rounded-md hover:bg-[#C41B14] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6708" data-mp="hgkw0i8uu76w">
                      {t('pages.ChallengeDispute.submit')}
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* No Evidence Warning Modal */}
        {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6709" data-mp="2y0wqlcvvg9ri">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6710" data-mp="21ady5w6igcs9">
              <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-6711" data-mp="25bfzqjnwh0xe">
                <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6712" data-mp="2ad5d45ylhqon"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6713" data-mp="2f82in2wo1hws">
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6714" data-mp="3qrez4tfd73cb">
                <div className="bg-white px-6 pt-6 pb-4" data-id="element-6715" data-mp="yjmivvh5v0t8">
                  <div className="flex items-start gap-4" data-id="element-6716" data-mp="1chta4srlu2jx">
                    <div className="flex-shrink-0" data-id="element-6717" data-mp="1cf6r9wz9k0me">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6718" data-mp="17hexexf264fk">
                        <AlertTriangleIcon size={24} className="text-red-600" data-id="element-6719" data-mp="1mefs99sjqfi9" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-6720" data-mp="1hejlj2sgs9z0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-6721" data-mp="1v8ap7ujikzmt">
                        {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-6722" data-mp="255p2xme1zabf">
                        {t('pages.DisputeDetail.submitting_a_challenge_without_supporting_evidence')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6723" data-mp="2osa7lqsqnkyy">
                  <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6724" data-mp="29v9djgn2rdxb">
                    {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                  </button>
                  <button onClick={() => {
                setShowNoEvidenceWarning(false);
                setChallengeStep('review');
              }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-6725" data-mp="25u7n9pb4yc7h">
                    {t('pages.ChallengeDispute.proceed_anyway')}
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-6726" data-mp="3otexiz4o1qmo">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-6727" data-mp="3k0luqgsrcsd8">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-6728" data-mp="3jz0zsq2j77eu">
          <ArrowLeftIcon size={20} data-id="element-6729" data-mp="3a3plu9yy6485" />
          {t('pages.DisputeDetail.back_to_disputes')}
        </button>
      </div>

      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6730" data-mp="bj7tf41wzcbi">
        <div className="flex items-start justify-between mb-6" data-id="element-6731" data-mp="6nip6ney978v">
          <div className="flex items-center gap-3" data-id="element-6732" data-mp="1mla4jp8ktbl">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6733" data-mp="1lbctwq7jp60">
              {t('pages.DisputeDetail.dispute')}
            </h1>
            {getStatusBadge(effectiveStatus)}
          </div>
          <button onClick={() => setShowHelpModal(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-6734" data-mp="2mewt0mdmgchr">
            {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
            <HelpCircleIcon size={16} data-id="element-6735" data-mp="11vkp1xlb6ll5" />
          </button>
        </div>

        <div className="mb-6" data-id="element-6736" data-mp="16tm7qhetoiw3">
          <div className="text-sm text-gray-500" data-id="element-6737" data-mp="16sb3yhldnb4z">
            {t('pages.ChallengeDispute.dispute_id')} {dispute.disputeId}
          </div>
        </div>

        {isChallenged && <div className="mb-6 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-6738" data-mp="1gjp852o9wz4g">
            <div className="flex items-start gap-4" data-id="element-6739" data-mp="1lhz5njxh0bt8">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-6740" data-mp="1pkvsjmu1mu2z">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6741" data-mp="1uki842xcgmey" />
              </div>
              <div className="flex-1" data-id="element-6742" data-mp="3rcicj4fipopy">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6743" data-mp="3rdk0g9hmu5p3">
                  {t('pages.DisputeDetail.challenge_submitted')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6744" data-mp="2239dgdcy52lj">
                  {t('pages.DisputeDetail.your_challenge_has_been_submitted_and')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors" data-id="element-6745" data-mp="2btu5tj211aw0">
                  <FileTextIcon size={18} data-id="element-6746" data-mp="1j6gga60nrhlx" />
                  {t('pages.DisputeDetail.view_challenge_details')}
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'won' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-6747" data-mp="1o3gmcgxickd1">
            <div className="flex items-start gap-4" data-id="element-6748" data-mp="34hgkbmcslzxn">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-6749" data-mp="34k2t1pbf6lhg">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6750" data-mp="2zl9hi2fp14jx" />
              </div>
              <div className="flex-1" data-id="element-6751" data-mp="ld8j5gb4n2k4">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6752" data-mp="qaqjiib2w9gi">
                  {t('pages.DisputeDetail.dispute_won')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6753" data-mp="ccir8nozlwbo">
                  {t('pages.DisputeDetail.the_issuing_bank_has_reviewed_your')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors" data-id="element-6754" data-mp="150693uyvi8um">
                  <FileTextIcon size={18} data-id="element-6755" data-mp="107v4wfx3o0w9" />
                  {t('pages.DisputeDetail.view_your_submitted_evidence')}
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'reversed' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-6756" data-mp="1sq0j84rt5jb6">
            <div className="flex items-start gap-4" data-id="element-6757" data-mp="32k0ukt0lnkhl">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-6758" data-mp="2xlplwlm1jg0y">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6759" data-mp="2sllcffjjvlk8" />
              </div>
              <div className="flex-1" data-id="element-6760" data-mp="2olavx8y4tcr4">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6761" data-mp="2ok86l5ggjidb">
                  {t('pages.DisputeDetail.dispute_reversed')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-6762" data-mp="2eug8i195g743">
                  {t('pages.DisputeDetail.the_cardholder_has_withdrawn_their_dispute')}
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'accepted' && <div className="mb-6 p-5 bg-[#FEE7E5] border-2 border-[#FBD4D1] rounded-lg" data-id="element-6763" data-mp="20u5hmy7uasix">
            <div className="flex items-start gap-4" data-id="element-6764" data-mp="1vvkjr5g28kxn">
              <div className="w-10 h-10 rounded-full bg-[#E2231A] flex items-center justify-center flex-shrink-0" data-id="element-6765" data-mp="1vy6xxnxghyou">
                <CheckCircleIcon size={20} className="text-gray-900" data-id="element-6766" data-mp="gd4od7j1n4cs" />
              </div>
              <div className="flex-1" data-id="element-6767" data-mp="gezagp61x2ig">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6768" data-mp="kehuliq1t9k8">
                  {t('pages.DisputeDetail.dispute_accepted')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-6769" data-mp="pbgn5wu2d8gb">
                  {t('pages.DisputeDetail.you_have_accepted_this_dispute_the')}
                </div>
              </div>
            </div>
          </div>}

        {isAccepted && <div className="mb-6 p-5 bg-[#FEE7E5] border-2 border-[#FBD4D1] rounded-lg" data-id="element-6770" data-mp="1n76tafmdcwk">
            <div className="flex items-start gap-4" data-id="element-6771" data-mp="6f8fynhhqnid">
              <div className="w-10 h-10 rounded-full bg-[#E2231A] flex items-center justify-center flex-shrink-0" data-id="element-6772" data-mp="6h1oy9fktjtj">
                <CheckCircleIcon size={20} className="text-gray-900" data-id="element-6773" data-mp="3a420q0efvmyi" />
              </div>
              <div className="flex-1" data-id="element-6774" data-mp="3a1pa6yum849u">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6775" data-mp="h8rzxk84nwdv">
                  {t('pages.DisputeDetail.dispute_accepted')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-6776" data-mp="c8nsejp76bzs">
                  {t('pages.DisputeDetail.you_have_accepted_this_dispute_the')}
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'lost' && <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-6777" data-mp="3ugwl1f859htt">
            <div className="flex items-start gap-4" data-id="element-6778" data-mp="3pi228da6ft9p">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-id="element-6779" data-mp="3pjd1bzsogcuh">
                <XCircleIcon size={20} className="text-white" data-id="element-6780" data-mp="2ckigro3t92a" />
              </div>
              <div className="flex-1" data-id="element-6781" data-mp="2enh203boaim">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6782" data-mp="1iprdz8bgqlak">
                  {t('pages.DisputeDetail.dispute_lost')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6783" data-mp="1dtty13443gyb">
                  {t('pages.DisputeDetail.the_issuing_bank_has_reviewed_your_2')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors" data-id="element-6784" data-mp="1caaigzqwrg0p">
                  <FileTextIcon size={18} data-id="element-6785" data-mp="24zrgqe17lr4u" />
                  {t('pages.DisputeDetail.view_your_submitted_evidence')}
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-6786" data-mp="1v8wlwl9wdj7s">
          <div data-id="element-6787" data-mp="207hjecxrlosj">
            <div className="text-sm text-gray-500 mb-1" data-id="element-6788" data-mp="206ydjule2quv">
              {t('pages.ChallengeDispute.dispute_amount')}
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-6789" data-mp="2nvr4fcn5m8im">
              {formatCurrency(dispute.amount)}
            </div>
          </div>
          <div data-id="element-6790" data-mp="142zknw5k57vc">
            <div className="text-sm text-gray-500 mb-1" data-id="element-6791" data-mp="143is2ixl0dv4">
              {t('pages.ChallengeDispute.response_due')}
            </div>
            <div className="text-base font-medium text-gray-900" data-id="element-6792" data-mp="1038pzhcwu7lj">
              {formatDateTime(dispute.dueDate)}
            </div>
            {hoursRemaining > 0 && hoursRemaining <= 24 && !isChallenged && <div className="text-sm text-red-600 font-medium" data-id="element-6793" data-mp="1nqql9mvdvnt9">
                {t('pages.DisputeDetail.due_in')} {hoursRemaining}{' '}
                {hoursRemaining === 1 ? 'hour' : 'hours'}
              </div>}
          </div>
        </div>

        {/* Purchasing-history challenge banner removed for demo */}

        {isChallenged && <div className="mb-6" data-id="element-6794" data-mp="19115ommjnh9g">
            <button onClick={handleViewChallengeDetails} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6795" data-mp="18zzsoclywkp3">
              {t('pages.DisputeDetail.view_challenge_details')}
            </button>
          </div>}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-6796" data-mp="2937dojdwn9oe">
          <div data-id="element-6797" data-mp="2dzxs72a29x7p">
            <div className="text-gray-500 mb-1" data-id="element-6798" data-mp="2e1imcqsgre51">
              {t('pages.DisputeDetail.reason')}
            </div>
            <div className="flex items-center gap-2" data-id="element-6799" data-mp="2iyiqgw5r66ti">
              <span className="font-medium text-gray-900" data-id="element-6800" data-mp="2nzg9fayl74w2">
                {getReasonLabel(dispute.reason)}
              </span>
              <div className="relative inline-block" data-id="element-6801" data-mp="2rzqp5fc6l9o4">
                <button onMouseEnter={() => setShowReasonTooltip(true)} onMouseLeave={() => setShowReasonTooltip(false)} className="inline-flex items-center" data-id="element-6802" data-mp="2wzbnponfvg0b">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-6803" data-mp="36od3petzhxea" />
                </button>
                {showReasonTooltip && <div className="absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-6804" data-mp="3fqw4lte7lzxw">
                    {getReasonDescription(dispute.reason)}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-6805" data-mp="1bnjctdpo1rb3"></div>
                  </div>}
              </div>
            </div>
          </div>
          <div data-id="element-6806" data-mp="1kld4ucgedysg">
            <div className="text-gray-500 mb-1" data-id="element-6807" data-mp="1kktpn3i4toi4">
              {t('pages.DisputeDetail.network_reason_code')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-6808" data-mp="1uczpt0ijfc8e">
              {dispute.networkReasonCode}
            </div>
          </div>
          <div data-id="element-6809" data-mp="28bhbksovueno">
            <div className="text-gray-500 mb-1" data-id="element-6810" data-mp="28bz53hk5uerw">
              {t('pages.DisputeDetail.dispute_stage')}
            </div>
            <div className="font-medium text-gray-900 capitalize" data-id="element-6811" data-mp="248t3wobwndsa">
              {dispute.stage.replace(/_/g, ' ')}
            </div>
          </div>
          <div data-id="element-6812" data-mp="9xnybeh9crab">
            <div className="text-gray-500 mb-1" data-id="element-6813" data-mp="9y7i70qgxpqz">
              {t('pages.DisputeDetail.dispute_time')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-6814" data-mp="3vz53ran1arg5">
              {formatDateTime(dispute.createdAt)}
            </div>
          </div>
          <div data-id="element-6815" data-mp="3m8sfp8g0gg59">
            <div className="text-gray-500 mb-1" data-id="element-6816" data-mp="3m9buwhea0qfl">
              {t('pages.DisputeDetail.transaction_type')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-6817" data-mp="3r629t1echfzf">
              {t('pages.DisputeDetail.payment')}
            </div>
          </div>
          <div data-id="element-6818" data-mp="3h9fqih1o2wzi">
            <div className="text-gray-500 mb-1" data-id="element-6819" data-mp="3h97aiga8zbk3">
              {t('pages.DisputeDetail.issuer_comments')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-6820" data-mp="33aps8y342r9k">
              {t('pages.DisputeDetail.recall')}
            </div>
          </div>
        </div>

        {dispute.status === 'requires_response' && !isChallenged && !isAccepted && <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200" data-id="element-6821" data-mp="16he6sv2hekpi">
              <button onClick={handleChallengeClick} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md" data-id="element-6822" data-mp="16f1sqszuguhq">
                {t('pages.DisputeDetail.challenge')}
              </button>
              <button onClick={handleAcceptClick} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-6823" data-mp="21aexxx21rfvq">
                {t('pages.DisputeDetail.accept')}
              </button>
            </div>}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-6824" data-mp="1mce9tp7eu8c5">
        <div className="border-b border-gray-200" data-id="element-6825" data-mp="1ibkliuq22riq">
          <nav className="flex gap-8 px-6" role="tablist" aria-label={t('pages.DisputeDetail.attrs.tabs')} data-id="element-6826" data-mp="1icvvjca3gvia">
            <button role="tab" aria-selected={activeTab === 'payment'} aria-controls="dispute-tabpanel" id="dispute-tab-payment" onClick={() => setActiveTab('payment')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payment' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6827" data-mp="3cpvar4akbnp1">
              {t('pages.DisputeDetail.payment_summary')}
            </button>
            <button role="tab" aria-selected={activeTab === 'related'} aria-controls="dispute-tabpanel" id="dispute-tab-related" onClick={() => setActiveTab('related')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6828" data-mp="fk9w3zharvg8">
              {t('pages.DisputeDetail.related_orders')}
            </button>
          </nav>
        </div>

        {/* Payment Summary Tab Content */}
        {activeTab === 'payment' && <div className="p-6" data-id="element-6829" data-mp="30yj5wgfted8k">
            <div className="space-y-4 text-sm" data-id="element-6830" data-mp="30xrap6q4qhit">
              <div data-id="element-6831" data-mp="310ls0pdujm80">
                <div className="text-gray-500 mb-1" data-id="element-6832" data-mp="2ma64v51ve477">
                  {t('pages.DisputeDetail.order_id')}
                </div>
                <div className="flex items-center gap-2" data-id="element-6833" data-mp="2r7y0a91q48g5">
                  <span className="font-medium text-gray-900" data-id="element-6834" data-mp="2d981moz9a0po">
                    {dispute.transactionId}
                  </span>
                  <div className="relative group" data-id="element-6835" data-mp="2hb1wggmrsahx">
                    <button onClick={() => handleCopyOrderId(dispute.transactionId)} className="text-gray-400 hover:text-blue-600 transition-colors" title={t('pages.DisputeDetail.attrs.copy_order_id')} aria-label={t('pages.DisputeDetail.attrs.copy_order_id')} data-id="element-6836" data-mp="2h8gulrmh1uk6">
                      {copiedOrderId === dispute.transactionId ? <CheckIcon size={16} className="text-green-600" aria-hidden="true" data-id="element-6837" data-mp="1qzzx71uhr6pd" /> : <CopyIcon size={16} aria-hidden="true" data-id="element-6838" data-mp="20re1ro1auwpd" />}
                    </button>
                    {copiedOrderId === dispute.transactionId && <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap" data-id="element-6839" data-mp="29q9953g2ycu9">
                        {t('pages.DisputeDetail.copied')}
                      </div>}
                  </div>
                </div>
              </div>
              <div data-id="element-6840" data-mp="2eqwy7jkr0jlu">
                <div className="text-gray-500 mb-1" data-id="element-6841" data-mp="2eoap3fi7lw1i">
                  {t('pages.DisputeDetail.source')}
                </div>
                <div className="flex items-center gap-2" data-id="element-6842" data-mp="kcm7f5klffdf">
                  {(() => {
                const {
                  name,
                  Icon
                } = getPaymentSource();
                return <>
                        <Icon size={16} className="text-gray-600" data-id="element-6843" data-mp="1e1jwyyjfh4oj" />
                        <span className="font-medium text-gray-900" data-id="element-6844" data-mp="1e3wpu6qq4zgd">
                          {name}
                        </span>
                      </>;
              })()}
                </div>
              </div>
              <div data-id="element-6845" data-mp="1t05lyzfbiwok">
                <div className="text-gray-500 mb-1" data-id="element-6846" data-mp="1sykq96h9p7p4">
                  {t('pages.DisputeDetail.payment_type')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-6847" data-mp="1wyuv2ftbps2e">
                  {t('pages.DisputeDetail.debit_card')}
                </div>
              </div>
              <div data-id="element-6848" data-mp="26ppkft1v97s2">
                <div className="text-gray-500 mb-1" data-id="element-6849" data-mp="26q94bfb2u68q">
                  {t('pages.DisputeDetail.payment_method')}
                </div>
                <div className="flex items-center gap-2" data-id="element-6850" data-mp="2goya49pp808m">
                  <span className="font-medium text-gray-900" data-id="element-6851" data-mp="2gpgdrreoxgqk">
                    {dispute.cardBrand} •••• {dispute.cardLast4}
                  </span>
                </div>
              </div>
              <div data-id="element-6852" data-mp="1p968cuwvbe">
                <div className="text-gray-500 mb-1" data-id="element-6853" data-mp="174qog1jase">
                  {t('pages.DisputeDetail.cardholder_name')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-6854" data-mp="3m9yk4zm0wwhr">
                  {dispute.customerName}
                </div>
              </div>
              <div data-id="element-6855" data-mp="nqrpncvr8i4b">
                <div className="text-gray-500 mb-1" data-id="element-6856" data-mp="nu5padd1ad82">
                  {t('pages.ChallengeDispute.customer_purchase_ip')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-6857" data-mp="e1rj98exakg3">35.221.170.121</div>
              </div>
              <div data-id="element-6858" data-mp="93q591wctbbh">
                <div className="text-gray-500 mb-1" data-id="element-6859" data-mp="925b3ddybue5">
                  {t('pages.DisputeDetail.customer_phone')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-6860" data-mp="16knei7ynqqoo">+65 9123 4567</div>
              </div>
              <div data-id="element-6861" data-mp="11prb6bnz0gw9">
                <div className="text-gray-500 mb-1" data-id="element-6862" data-mp="m9uc9lpykob7">
                  {t('pages.DisputeDetail.security_checks')}
                </div>
                <div className="space-y-1" data-id="element-6863" data-mp="r9yv3iec9v4l">
                  <div className="flex items-center gap-2 text-green-600" data-id="element-6864" data-mp="r6ujw12ghz4f">
                    <CheckCircleIcon size={16} data-id="element-6865" data-mp="cd7sl09dc3ar" />
                    <span data-id="element-6866" data-mp="hahql4atpna7">{t('pages.DisputeDetail.card_security_code')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-6867" data-mp="150lvdezqzt2s">
                    <XCircleIcon size={16} data-id="element-6868" data-mp="152ohhnxs5228" />
                    <span data-id="element-6869" data-mp="19zopi4e6wevz">{t('pages.DisputeDetail.3ds')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-6870" data-mp="vb2c4884hx7e">
                    <XCircleIcon size={16} data-id="element-6871" data-mp="109ly28mpv8zr" />
                    <span data-id="element-6872" data-mp="108kc3945x239">
                      {t('pages.DisputeDetail.billing_address_verification')}
                    </span>
                  </div>
                </div>
              </div>
              <a href="#" className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-block" data-id="element-6873" data-mp="1sqyab9teuq2v">
                {t('pages.DisputeDetail.view_payment_details')}
              </a>
            </div>
          </div>}

        {/* Related Orders Tab Content */}
        {activeTab === 'related' && <div className="p-6" data-id="element-6874" data-mp="3i71xppwnf027">
            <div className="mb-4 text-sm text-gray-600" data-id="element-6875" data-mp="3i6s6y03s4ovg">
              {t('pages.DisputeDetail.customers_may_have_a_purchasing_history')}{' '}
              <span className="font-medium" data-id="element-6876" data-mp="2ujk2fke6djuh">
                {t('pages.DisputeDetail.matching_transactions_to_the_issuer')}
              </span>
              .{' '}
              <div className="relative inline-block" data-id="element-6877" data-mp="33j77cg6457sa">
                <button onMouseEnter={() => setShowMatchingCriteriaTooltip(true)} onMouseLeave={() => setShowMatchingCriteriaTooltip(false)} className="inline-flex items-center" data-id="element-6878" data-mp="2zgka1zqoaunp">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-6879" data-mp="epjrfmwiy13w" />
                </button>
                {showMatchingCriteriaTooltip && <div className="absolute z-10 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-6880" data-mp="3ci211d23gjd4">
                    <div className="space-y-2" data-id="element-6881" data-mp="3lkb6hoii9mjn">
                      <p data-id="element-6882" data-mp="3lk1ha35abjf0">
                        {t('pages.DisputeDetail.to_qualify_as_a_matching_transaction')}
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-6883" data-mp="2tu6ofwi7xbll">
                        <li data-id="element-6884" data-mp="2xuguta9xa40z">
                          {t('pages.DisputeDetail.at_least_two_data_elements_ip')}
                        </li>
                        <li data-id="element-6885" data-mp="264ch1ugo4f8q">
                          {t('pages.DisputeDetail.between_120_and_365_days_older')}
                        </li>
                        <li data-id="element-6886" data-mp="22mexa6r1d1rv">
                          {t('pages.DisputeDetail.undisputed_and_not_have_been_reported')}
                        </li>
                      </ul>
                      <p data-id="element-6887" data-mp="1tjvvlpyzkv77">{t('pages.DisputeDetail.we_also_show_you_if_the')}</p>
                    </div>
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-6888" data-mp="1olm0tgh6bwm4"></div>
                  </div>}
              </div>
            </div>

            {/* Related Orders Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-6889" data-mp="19x7q6jdin3v0">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-6890" data-mp="19v53a87ntqui">
                <thead className="bg-gray-50" data-id="element-6891" data-mp="15wxmxeh3yrju">
                  <tr data-id="element-6892" data-mp="10wjjk9qgc5s5">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6893" data-mp="10wt1da1cf6mr">
                      {t('pages.DisputeDetail.order_id')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6894" data-mp="2jtwe1bqut23s">
                      {t('pages.DisputeDetail.amount')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6895" data-mp="2au9bunqavs9o">
                      {t('pages.DisputeDetail.created_time')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6896" data-mp="213en9cpl0gl2">
                      {t('pages.DisputeDetail.status')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6897" data-mp="1s3rmmt4ofet2">
                      {t('pages.DisputeDetail.matching_criteria')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6898" data-mp="1i6vkd55dfowy">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50" data-id="element-6899" data-mp="13htwo8erq552">
                      <td className="px-4 py-3" data-id="element-6900" data-mp="13k6emlkj0fi4">
                        <div className="flex items-center gap-2" data-id="element-6901" data-mp="yjsia0saetzx">
                          <span className="text-sm text-gray-900" data-id="element-6902" data-mp="1dg9s2nktw0kd">
                            {order.orderId}
                          </span>
                          <button className="text-blue-600 hover:text-blue-700" data-id="element-6903" data-mp="18g5bkxjx75u5">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6904" data-mp="1mg69c44w1x5e">
                              <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6905" data-mp="1r7eir067xiik" />

                              <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6906" data-mp="2mnz40hldqqze" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-6907" data-mp="3hym4kdgawf7u">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-6908" data-mp="3mr6u32s6c65k">
                        {order.createdTime}
                      </td>
                      <td className="px-4 py-3" data-id="element-6909" data-mp="3dw8zzsdaincn">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-6910" data-mp="3dx0j43ppasm1">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-6911" data-mp="3rofela0som9z">
                        <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6912" data-mp="3rnxbpujmn9t3">
                          {order.matchingCriteria} {t('pages.DisputeDetail.of')}{' '}
                          {order.totalCriteria}{' '}
                          {t('pages.DisputeDetail.criteria')}
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-6913" data-mp="1sbu6sixlny1t">
                <div className="flex items-center gap-2" data-id="element-6914" data-mp="1nchjjx2tgoyp">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6915" data-mp="1igk0hj0654i8">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6916" data-mp="14fpv798f3jns">
                      <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6917" data-mp="2yv24gcrx4cny" />

                      <path d="M5 12L5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6918" data-mp="3g2tbiedeq2cf" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6919" data-mp="2sea5y6lqs3o1">
                    <ChevronLeftIcon size={16} data-id="element-6920" data-mp="clb6eep0r9fp" />
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`} data-id="element-6921" data-mp="a7xuji1aef27">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6922" data-mp="y3k1b6pqr6wp">
                    <ChevronRightIcon size={16} data-id="element-6923" data-mp="1gvcxfa03x88n" />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6924" data-mp="1cusyc0vz3ujv">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6925" data-mp="20fk5300jxwlz">
                      <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6926" data-mp="1cranybea9x16" />

                      <path d="M11 4L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6927" data-mp="17szc5v4fhcgb" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-4" data-id="element-6928" data-mp="30jb5afqr8vvm">
                  <div className="text-sm text-gray-700" data-id="element-6929" data-mp="2vqi1bu341rkl">
                    {t('pages.DisputeDetail.rows_per_page')}{' '}
                    <span className="font-medium" data-id="element-6930" data-mp="2rqhjdzgsyy8s">{rowsPerPage}</span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-6931" data-mp="2mrehgnw4d851">
                    {(currentPage - 1) * rowsPerPage + 1} -{' '}
                    {Math.min(currentPage * rowsPerPage, relatedOrders.length)}{' '}
                    {t('pages.DisputeDetail.of')} {relatedOrders.length}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Breakdown Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6932" data-mp="35ixiwqahwe4y">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6933" data-mp="1z1k3g302ddj7">
          {t('pages.DisputeDetail.payment_breakdown')}
        </h3>
        <div className="space-y-3 max-w-md" data-id="element-6934" data-mp="1u40rj9qu9cuf">
          <div className="flex items-center justify-between" data-id="element-6935" data-mp="1jyiqhj2mpsv6">
            <div className="text-sm text-gray-600" data-id="element-6936" data-mp="1owtxzn5gbra8">
              {t('pages.DisputeDetail.payment_amount')}
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6937" data-mp="1tvdp01ywjt9g">
              {formatCurrency(paymentAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6938" data-mp="1ynyc6knaz840">
            <div className="flex items-center gap-1" data-id="element-6939" data-mp="22p0dr84ozw95">
              <span className="text-sm text-gray-600" data-id="element-6940" data-mp="22ql8oyux5h7j">
                {t('pages.DisputeDetail.final_disputed_amount')}
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6941" data-mp="2cm54zmdeahwt" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6942" data-mp="2hl7w02wjinls">
              - {formatCurrency(disputedAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6943" data-mp="i9yg5krkw2m1">
            <div className="flex items-center gap-1" data-id="element-6944" data-mp="n6yndz05zbeq">
              <span className="text-sm text-gray-600" data-id="element-6945" data-mp="n9kslrzl1ets">
                {t('pages.DisputeDetail.dispute_fee')}
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6946" data-mp="2k67a7ljbnlp8" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6947" data-mp="2k438na8ways4">
              - {formatCurrency(disputeFee)}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-6948" data-mp="2tvh496xq7efz">
            <div className="flex items-center justify-between" data-id="element-6949" data-mp="2ty3cl8sfxxz9">
              <div className="text-sm font-semibold text-gray-900" data-id="element-6950" data-mp="266ofbufiu6uv">
                {t('pages.DisputeDetail.net_amount')}
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-6951" data-mp="2f6uqvarnswre">
                - {formatCurrency(netAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6952" data-mp="3qlgklnk4bh94">
        <div className="flex items-center justify-between mb-4" data-id="element-6953" data-mp="3mkelr7u4577o">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-6954" data-mp="3mjv0pi9622pf">
            {t('pages.DisputeDetail.recent_activity')}
          </h3>
          <button onClick={() => setShowAddNote(!showAddNote)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors" data-id="element-6955" data-mp="2xmfsgbmfq5a">
            <PenIcon size={16} data-id="element-6956" data-mp="3p52tcx7pye66" />
            {t('pages.DisputeDetail.add_note')}
          </button>
        </div>

        {/* Add Note Form */}
        {showAddNote && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-6957" data-mp="ln2kzgy946s1">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={t('pages.DisputeDetail.attrs.add_a_note_for_internal_reference')} rows={3} className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3" data-id="element-6958" data-mp="6zzlx5ua5q2l" />

            <div className="flex items-center justify-end gap-2" data-id="element-6959" data-mp="26e2y2p2flsqa">
              <button onClick={() => {
            setShowAddNote(false);
            setNoteText('');
          }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6960" data-mp="2bcxgvr0efhae">
                {t('pages.ChallengeDispute.cancel')}
              </button>
              <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6961" data-mp="1nhbcua3bx3jl">
                {t('pages.DisputeDetail.add_note')}
              </button>
            </div>
          </div>}

        <div className="space-y-4" data-id="element-6962" data-mp="38sl2l1th76v7">
          {reversedLifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-6963" data-mp="3dqmmtq2n1c89" data-mpi={index}>
              <div className="flex flex-col items-center" data-id="element-6964" data-mp="124kpdbkwokbw">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${event.status === 'note' ? 'bg-purple-100' : index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-6965" data-mp="1241cwae0yo59">
                  {event.status === 'note' ? <FileTextIcon size={16} className="text-purple-600" data-id="element-6966" data-mp="newsqnno69ui" /> : <ClockIcon size={16} className={index === 0 ? 'text-blue-600' : 'text-gray-400'} data-id="element-6967" data-mp="ihwhzzfvkix2" />}
                </div>
                {index < reversedLifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-6968" data-mp="4in2z33enuuo"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-6969" data-mp="23tob07r3q0q3">
                <div className="flex items-start justify-between gap-2" data-id="element-6970" data-mp="1yvczlsl5ri5r">
                  <div className="flex-1" data-id="element-6971" data-mp="1yt0mbsqchvz1">
                    {event.status === 'note' && <div className="flex items-center gap-2 mb-1" data-id="element-6972" data-mp="3t98p1vir8un0">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-6973" data-mp="1xfocs1otjwx">
                          {event.isPinned && <PinIcon size={12} data-id="element-6974" data-mp="3obpaeui5afuj" />}
                          {t('pages.DisputeDetail.internal_note')}
                        </span>
                      </div>}
                    {editingNoteId === event.id ? <div className="space-y-2" data-id="element-6975" data-mp="kuqn8bjeqbbu">
                        <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-6976" data-mp="5yr4m6yo8ssg" />

                        <div className="flex items-center gap-2" data-id="element-6977" data-mp="1dpof1xti29eb">
                          <button onClick={() => handleSaveEdit(event.id)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-6978" data-mp="1ds16r2p27y4k">
                            {t('pages.DisputeDetail.save')}
                          </button>
                          <button onClick={handleCancelEdit} className="px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900" data-id="element-6979" data-mp="uyxaraodd96s">
                            {t('pages.ChallengeDispute.cancel')}
                          </button>
                        </div>
                      </div> : <>
                        <div className="text-sm font-medium text-gray-900" data-id="element-6980" data-mp="ba4x59xyvwoi">
                          {event.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-6981" data-mp="jzdc53zl4v2m">
                          {formatDateTime(event.timestamp)}
                          {event.status === 'note' && event.author && <span data-id="element-6982" data-mp="txjchh5qtt5d">
                              {' '}
                              {t('pages.DisputeDetail.by')} {event.author}
                            </span>}
                        </div>
                      </>}
                  </div>
                  {event.status === 'note' && editingNoteId !== event.id && <div className="relative note-menu-container" data-id="element-6983" data-mp="1cnraxlyhzvf8">
                      <button onClick={() => setShowNoteMenu(showNoteMenu === event.id ? null : event.id)} aria-label="Note actions" aria-expanded={showNoteMenu === event.id} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" data-id="element-6984" data-mp="1cok9hznazema">
                        <MoreVerticalIcon size={16} aria-hidden="true" data-id="element-6985" data-mp="3737l6mslxch5" />
                      </button>
                      {showNoteMenu === event.id && <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-6986" data-mp="2x61tbcc65hfv">
                          <button onClick={() => handlePinNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6987" data-mp="3usipufd2vr0k">
                            <PinIcon size={14} data-id="element-6988" data-mp="3fwb3p9i40fin" />
                            {event.isPinned ? 'Unpin from top' : 'Pin to the top'}
                          </button>
                          <button onClick={() => handleEditNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-6989" data-mp="24guuec8sypu3">
                            <PenIcon size={14} data-id="element-6990" data-mp="10gxwl39vvwr3" />
                            {t('pages.DisputeDetail.edit')}
                          </button>
                          <button onClick={() => handleDeleteNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6991" data-mp="rhjaeg0r28ce">
                            <TrashIcon size={14} data-id="element-6992" data-mp="3t04g756a7nh" />
                            {t('pages.DisputeDetail.delete')}
                          </button>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6993" data-mp="3bdj1ci3v4r0z">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6994" data-mp="3bdswek3rlodj">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeModal(false)} data-id="element-6995" data-mp="37eab3n817ba6">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6996" data-mp="3gbklz7gxsyek"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6997" data-mp="3ublqcwweunys">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6998" data-mp="7xc16lknpbwy">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6999" data-mp="huzzflhh11jv">
                <div className="flex items-start justify-between mb-4" data-id="element-7000" data-mp="hvhz6s3cdxwj">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-7001" data-mp="cwye0twkoq58">
                    {t('pages.DisputeDetail.you_are_challenging_the_dispute')}
                  </h2>
                  <button onClick={() => setShowChallengeModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-7002" data-mp="2yns1r77kacba">
                    <CloseIcon size={20} data-id="element-7003" data-mp="33l239l886efh" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-7004" data-mp="38k55yz0qg8ka">
                  {t('pages.DisputeDetail.heres_a_preview_of_what_will')}
                </p>

                <div className="space-y-6" data-id="element-7005" data-mp="2fwr7gwhdz4y2">
                  <div className="flex gap-4" data-id="element-7006" data-mp="2fvg2itc7fr5d">
                    <div className="flex flex-col items-center" data-id="element-7007" data-mp="2kwdhyy5ty736">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-7008" data-mp="2kusqjhetb49j">
                        <PenIcon size={20} className="text-blue-600" data-id="element-7009" data-mp="1s8hnm03qgr9k" />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-7010" data-mp="1x6j6ajx8yoki"></div>
                    </div>
                    <div className="flex-1 pb-6" data-id="element-7011" data-mp="ecgd9xcfdbst">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7012" data-mp="eb57jrzf5tz2">
                        {t('pages.DisputeDetail.you_submit_the_challenge')}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-7013" data-mp="59ys7m8iap7e">
                        {t('pages.DisputeDetail.well_guide_you_to_provide_the')}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4" data-id="element-7014" data-mp="3rjrk86b5l5gl">
                    <div className="flex flex-col items-center" data-id="element-7015" data-mp="3mr83j0h65u7k">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-7016" data-mp="3mpx1b53dguik">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400" data-id="element-7017" data-mp="3drbo7pgo67p6"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-7018" data-mp="38rgvrhh8xa20">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7019" data-mp="33svxvopgv2gq">
                        {t('pages.DisputeDetail.issuer_reviews_the_evidence_decides_on')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-7020" data-mp="25o1z8vctwat4">
                        {t('pages.DisputeDetail.if_the_issuer_is_convinced_by')}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-7021" data-mp="1vrfdm4cohfq1">
                        {t('pages.DisputeDetail.if_they_are_not_convinced_by')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-7022" data-mp="2od8m8uknnokq">
                <button onClick={() => setShowChallengeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-7023" data-mp="2jef7kytmtriz">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
                <button onClick={handleChallengeConfirm} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-7024" data-mp="13yom275xwf74">
                  {t('pages.DisputeDetail.challenge')}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Evidence Form Modal - keeping existing */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7025" data-mp="27x9evdoglc7">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7026" data-mp="27f293ph8mpi">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-7027" data-mp="794fmq069cgr">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7028" data-mp="g7y947i7hmq3"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-7029" data-mp="2d0i1b6cpo7n1">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-7030" data-mp="3l9p4n78ycu06">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-7031" data-mp="3uanck0mihlo1">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-7032" data-mp="3ubyfjy84upe3">
                  {t('pages.DisputeDetail.submit_evidence')}
                </h3>
                <div className="space-y-4" data-id="element-7033" data-mp="82nim7gewhb8">
                  <div data-id="element-7034" data-mp="81cajvfxoneb">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7035" data-mp="34lx7fvik5wl">
                      {t('pages.DisputeDetail.customer_communication')}
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.DisputeDetail.attrs.describe_any_communication_with_the_customer')} data-id="element-7036" data-mp="gxlarcbqjq1i" />
                  </div>
                  <div data-id="element-7037" data-mp="1ag9q5azc97nl">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7038" data-mp="1aef57wo2hfji">
                      {t('pages.DisputeDetail.shippingdelivery_documentation')}
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.DisputeDetail.attrs.tracking_numbers_delivery_confirmation_etc')} data-id="element-7039" data-mp="10gixleuc92q5" />
                  </div>
                  <div data-id="element-7040" data-mp="hrc735naz6ts">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7041" data-mp="3r9u9xtur3le">
                      {t('pages.DisputeDetail.upload_supporting_documents')}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-7042" data-mp="7uppcpq9wvvz">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-7043" data-mp="rj5rxndo9m1" />

                      <div className="text-sm text-gray-600" data-id="element-7044" data-mp="3n3yh1ema2tta">
                        {t('pages.DisputeDetail.drag_and_drop_files_here_or')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-7045" data-mp="3e54fd9u3bd8v">
                        {t('pages.DisputeDetail.pdf_jpg_png_up_to_10mb')}
                      </div>
                      <input type="file" multiple className="hidden" aria-label="Upload evidence files" data-id="element-7046" data-mp="397uqpweiz5m3" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-7047" data-mp="xfxt4t33t1jf">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-7048" data-mp="xe4jd4x72177">
                  {t('pages.DisputeDetail.submit_evidence')}
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-7049" data-mp="qzpc62io1ex0">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Matching Criteria Side Panel */}
      {selectedOrder && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-7050" data-mp="1hp0cotq5wppt">
          <div className="absolute inset-0 overflow-hidden" data-id="element-7051" data-mp="1wnlxsjixlxkk">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} data-id="element-7052" data-mp="1wkzpuis4pg1t"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-7053" data-mp="13ywss9er983j">
              <div className="w-screen max-w-md" data-id="element-7054" data-mp="z0d626sc7sa4">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto" data-id="element-7055" data-mp="yxh6yf4r6xk3">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200" data-id="element-7056" data-mp="18xrlc32iyb0u">
                    <div className="flex items-center justify-between" data-id="element-7057" data-mp="18vn8gvmne1of">
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-7058" data-mp="l6chg5v3nlnh">
                        {t('pages.DisputeDetail.related_order')}
                      </h2>
                      <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600" data-id="element-7059" data-mp="2rvw87exxy54v">
                        <CloseIcon size={20} data-id="element-7060" data-mp="2hyi5ei4xxev1" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-6 py-4" data-id="element-7061" data-mp="2mv07194z4x35">
                    {/* Order Status */}
                    <div className="mb-6" data-id="element-7062" data-mp="3angrtrg3j365">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-7063" data-mp="3fma45gjnco4q">
                        {t('pages.DisputeDetail.order')}
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase" data-id="element-7064" data-mp="31o22ivbo0yn8">
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Order ID */}
                    <div className="mb-6" data-id="element-7065" data-mp="1u9qia567jmp4">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7066" data-mp="2lvqn3zel7eml">
                        {t('pages.DisputeDetail.order_id')}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-7067" data-mp="2gvkmvuu94jsw">
                        <span className="text-sm font-medium text-gray-900" data-id="element-7068" data-mp="2cuipzknt4ju3">
                          {selectedOrder.orderId}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7069" data-mp="27yk17vykgzt5">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7070" data-mp="34louseeh35pp">
                            <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7071" data-mp="2quteih1n9mc3" />

                            <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7072" data-mp="4mdtl9e2z37m" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Matching Criteria */}
                    <div className="mb-6" data-id="element-7073" data-mp="242adycchty5f">
                      <div className="flex items-center gap-2 mb-3" data-id="element-7074" data-mp="284oyr1zq37zc">
                        <h3 className="text-sm font-semibold text-gray-900" data-id="element-7075" data-mp="28604v8gn4rtm">
                          {t('pages.DisputeDetail.matching_criteria_2')}
                        </h3>
                        <InfoIcon size={14} className="text-gray-400" data-id="element-7076" data-mp="1z4s6yri1dt02" />
                      </div>

                      <div className="space-y-6" data-id="element-7077" data-mp="2muw6ons3t8ls">
                        {matchingCriteriaDetails.map((category, idx) => <div key={idx} data-id="element-7078" data-mp="2ruqyctjpe27w" data-mpi={idx}>
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-7079" data-mp="2d4a0fk6a1ufr">
                              {category.category}
                            </div>
                            {category.subtitle && <div className="text-xs text-gray-500 mb-2" data-id="element-7080" data-mp="35rnnmqk6bbmo">
                                {category.subtitle}
                              </div>}
                            <div className="space-y-2" data-id="element-7081" data-mp="z358bn29fgnj">
                              {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center gap-2" data-id="element-7082" data-mp="u4ty3b81z44s" data-mpi={itemIdx}>
                                  {item.matched ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-7083" data-mp="g4swftjyrso9" /> : <XCircleIcon size={16} className="text-red-600" data-id="element-7084" data-mp="p7k9fxshmwi0" />}
                                  <span className="text-sm text-gray-700" data-id="element-7085" data-mp="20mz4gw14zeah">
                                    {item.label}
                                  </span>
                                </div>)}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-7086" data-mp="178y5ldoceiok">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-7087" data-mp="176281qgepw0n">
                        {t('pages.DisputeDetail.payment_summary')}
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-7088" data-mp="ekacwx19kr0z">
                        <div className="flex justify-between" data-id="element-7089" data-mp="jibwrk6ikudi">
                          <span className="text-gray-600" data-id="element-7090" data-mp="jh0trmkw7qng">
                            {t('pages.DisputeDetail.payment_created_time')}
                          </span>
                          <span className="text-gray-900" data-id="element-7091" data-mp="3mz16ff1e8k7e">
                            2026-04-15
                            <br data-id="element-7092" data-mp="3rx2opxqzwfht" />
                            {t('pages.DisputeDetail.0901_utc0800')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-7093" data-mp="3ffjkjv0ofi7o">
                          <span className="text-gray-600" data-id="element-7094" data-mp="3fdyrk9u0g7bx">
                            {t('pages.DisputeDetail.payment_amount')}
                          </span>
                          <span className="text-gray-900" data-id="element-7095" data-mp="36elbuzfammir">
                            {formatCurrency(selectedOrder.amount, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-7096" data-mp="2rr8p57dr2aqs">
                          <span className="text-gray-600" data-id="element-7097" data-mp="2mrw32oupd7p9">
                            {t('pages.DisputeDetail.card_type')}
                          </span>
                          <span className="text-gray-900" data-id="element-7098" data-mp="2iozi4rhowzi5">
                            {t('pages.DisputeDetail.credit')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-7099" data-mp="ho2yshtxqx3x">
                          <span className="text-gray-600" data-id="element-7100" data-mp="lpo8xvf34xaf">
                            {t('pages.DisputeDetail.payment_method')}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-7101" data-mp="7vx6t83oofoq">
                            <span className="text-blue-600 font-medium" data-id="element-7102" data-mp="cv9shpitjgpq">
                              {t('pages.DisputeDetail.visa')}
                            </span>
                            <span className="text-gray-900" data-id="element-7103" data-mp="2ywzyuz0f8wk">
                              {t('pages.DisputeDetail.visa_9400')}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between" data-id="element-7104" data-mp="3q45udp7rwvbw">
                          <span className="text-gray-600" data-id="element-7105" data-mp="1jcdf8hgtlw23">
                            {t('pages.DisputeDetail.cardholder_name')}
                          </span>
                          <span className="text-gray-900" data-id="element-7106" data-mp="1o9x2g6vhxj66">
                            {t('pages.DisputeDetail.auto_testing')}
                          </span>
                        </div>
                        <div data-id="element-7107" data-mp="1tioe2jm0gv7c">
                          <div className="text-gray-600 mb-2" data-id="element-7108" data-mp="2788dbfgdvabw">
                            {t('pages.DisputeDetail.payment_security_check')}
                          </div>
                          <div className="space-y-1" data-id="element-7109" data-mp="22ff8krkx01zt">
                            <div className="flex items-center gap-2" data-id="element-7110" data-mp="2h5mszcuhfv35">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-7111" data-mp="2c7bgsvgpt8hr" />

                              <span className="text-gray-700 text-xs" data-id="element-7112" data-mp="2q4rrij61nofb">
                                {t('pages.DisputeDetail.card_security_code')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-7113" data-mp="rrrf5pap5wc6">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-7114" data-mp="rtkpbekiqyox" />

                              <span className="text-gray-700 text-xs" data-id="element-7115" data-mp="2vvumpccj6mr2">
                                {t('pages.DisputeDetail.3ds')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-7116" data-mp="35utnk8r21e3i">
                              <XCircleIcon size={14} className="text-gray-400" data-id="element-7117" data-mp="39u2hbdm3hbz2" />

                              <span className="text-gray-700 text-xs" data-id="element-7118" data-mp="3jl6rm00ts8nk">
                                {t('pages.DisputeDetail.billing_address_verification')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-7119" data-mp="1lyw8xohfp169">
                      <button onClick={() => setSelectedOrder(null)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-7120" data-mp="1hug5ruz7g7uq">
                        {t('pages.DisputeDetail.close')}
                      </button>
                      <button className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-7121" data-mp="25kiuc13wuvol">
                        {t('pages.DisputeDetail.view_payment_details')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Challenge Details Modal */}
      {showChallengeDetails && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7122" data-mp="2odwj1oh165vr">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7123" data-mp="2jk1yyjxijow5">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeDetails(false)} data-id="element-7124" data-mp="1rvyxhmth75kz">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7125" data-mp="1ixdks8aoqks4"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-7126" data-mp="1my5sgx3wshma">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-7127" data-mp="2fn4e4478q1wc">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-7128" data-mp="21pfxj8k7hgx3">
                <div className="flex items-start justify-between mb-6" data-id="element-7129" data-mp="26ldcpbjqgh8a">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-7130" data-mp="q7n5hvxbhcuf">
                    {t('pages.ChallengeDispute.challenge_details')}
                  </h2>
                  <button onClick={() => setShowChallengeDetails(false)} className="text-gray-400 hover:text-gray-600" data-id="element-7131" data-mp="v56hsqagffjq">
                    <CloseIcon size={24} data-id="element-7132" data-mp="io43068gyqvg" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-7133" data-mp="wn3tzkjgfhus">
                  <div data-id="element-7134" data-mp="rpc1opewdtq2">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-7135" data-mp="rotyt9xqch96">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-7136" data-mp="3v62cripc6wym">
                      <div className="flex" data-id="element-7137" data-mp="3v7delcz81un3">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7138" data-mp="3v47s85hz96v2">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7139" data-mp="8x13v9z3olyt">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7140" data-mp="28vwg0enlm7sw">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7141" data-mp="23wjym9fhxsxp">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7142" data-mp="2dt6gqqgfu5w1">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7143" data-mp="2mm9oci4ejoir">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7144" data-mp="31jsb5exirrnk">
                          {t('pages.ChallengeDispute.supporting_evidence')}
                        </span>
                        <div className="flex-1" data-id="element-7145" data-mp="17618x0hisa25">
                          <div className="flex items-center gap-3" data-id="element-7146" data-mp="176u5x9qofl73">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-7147" data-mp="127hh4jg8w41v">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7148" data-mp="3dvmec9oxm3g5">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7149" data-mp="391qjhg3yswp7" />

                                <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7150" data-mp="ocawkw7v9s4u" />
                              </svg>
                            </div>
                            <div className="flex-1" data-id="element-7151" data-mp="7zzb4t53tusb">
                              <div className="text-sm font-medium text-gray-900" data-id="element-7152" data-mp="15molbq9yndp9">
                                {t('pages.DisputeDetail.ohmmcairospecsshe9jul25pdf')}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-7153" data-mp="vv2339z7ged0">
                                {t('pages.DisputeDetail.104_mb')}
                              </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-700" data-id="element-7154" data-mp="1of8z00h39c8h">
                              <EyeIcon size={18} data-id="element-7155" data-mp="1jkwf5pbp9yw7" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-7156" data-mp="1vyrqkv4eedso">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-7157" data-mp="20w1jwluwr9ls">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-7158" data-mp="25siadmpky021">
                      <div className="flex" data-id="element-7159" data-mp="2aswkdaaqrk2p">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7160" data-mp="2as4ywsauz2q5">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7161" data-mp="2jn1dy1h9f9lx">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount)}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7162" data-mp="2yiqvnnfex0ku">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7163" data-mp="2yjsa81vn057b">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7164" data-mp="3qbawhmvj3mea">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7165" data-mp="3cb9ozokrdgpu">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7166" data-mp="3ce5ld8gyjxc6">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7167" data-mp="3leapn9quufm4">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7168" data-mp="10l7fg8fwnkvh">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7169" data-mp="wkx6cmwtgece">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7170" data-mp="146so2i01ewkq">
                          {t('pages.DisputeDetail.customerpomelopaycom')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-7171" data-mp="u8mmk1i57sge">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7172" data-mp="z4jubjfcbqhk">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-7173" data-mp="1rua81rd28qd5">
                          192.168.1.1
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}